import React, { useEffect } from 'react';
import { useState } from 'react';
import { ButtonType, ButtonTemplate, TEMPLATES } from './boon-button-templates';
import _uniqueId from 'lodash/uniqueId';

import styles from './boon-button.styles';
import {
    flattenClassesToString,
    resolveProperty,
    toBackgroundClass,
    toGroupHoverBackgroundClass,
    toGroupHoverTextClass,
    toTextClass,
} from './style-sheet';
import Link from 'next/link';

interface BoonButtonProps {
    onClick?: (event: React.MouseEvent) => void;
    style: ButtonTemplate;
    type: ButtonType;
    href?: string;
    as?: string;

    height?: string;
    width?: string;

    applyGapFix?: boolean;

    fontColor?: string;
    backgroundColor?: string;

    hoverFontColor?: string;
    hoverBackgroundColor?: string;

    addtlContainerClasses?: string;
    addtlContentClasses?: string;

    disabled?: boolean;

    font?: string;
}

const BoonButton: React.FC<BoonButtonProps> = props => {
    const [submitButtonId] = useState(_uniqueId('boon-btn-sbmt-'));

    const resolvePropertyValue = (propertyName: string) => {
        return resolveProperty(propertyName, props, TEMPLATES[props.style]);
    };

    const [containerStyle, setContainerStyle] = useState('invisible');
    const [curlyAlignStyle, setCurlyAlignStyle] = useState('');
    const [curlyAlignSvgStyle, setCurlyAlignSvgStyle] = useState('');
    const [gapStyleLeft, setGapStyleLeft] = useState('');
    const [contentStyle, setContentStyle] = useState('');
    const [gapStyleRight, setGapStyleRight] = useState('');
    const [curlyImgStyle, setCurlyImgStyle] = useState('');
    const [curlySvgStyle, setCurlySvgStyle] = useState('');
    const [href, setHref] = useState('');

    const renderStyles = () => {
        const applyGapFix = props.applyGapFix === true || props.applyGapFix === undefined || props.applyGapFix === null;

        const backgroundProperty = props.disabled === true ? 'disabledBackgroundClass' : 'backgroundColor';
        const cursorStyle = props.disabled === true ? 'cursor-not-allowed' : 'cursor-pointer';

        const gapFixAlign = applyGapFix ? 'twopx-right' : '';
        const gapFixGapLeft = applyGapFix ? 'onepx-right' : '';
        const gapFixGapRight = applyGapFix ? 'onepx-left' : '';
        const gapFixCurly = applyGapFix ? 'twopx-left' : '';

        const height = resolvePropertyValue('height');
        const width = resolvePropertyValue('width');
        const color = toTextClass(resolvePropertyValue('fontColor'));
        const backgroundColor = toBackgroundClass(resolvePropertyValue(backgroundProperty));
        const fillColor = toTextClass(resolvePropertyValue(backgroundProperty));

        const hoverColor = toGroupHoverTextClass(props.disabled === true ? '' : resolvePropertyValue('hoverFontColor'));
        const hoverBackgroundColor = toGroupHoverBackgroundClass(
            props.disabled === true ? '' : resolvePropertyValue('hoverBackgroundColor')
        );
        const hoverFillColor = toGroupHoverTextClass(
            props.disabled === true ? '' : resolvePropertyValue('hoverBackgroundColor')
        );

        const addtlContainerClasses = resolvePropertyValue('addtlContainerClasses');
        const addtlContentClasses = resolvePropertyValue('addtlContentClasses');

        const font = resolvePropertyValue('font');

        const gapSize = props.width ? '' : 'px-1.5em';

        setContainerStyle(
            flattenClassesToString(
                'BoonButton group z-10 inline-flex flex-grow-0 flex-shrink-0 flex-nowrap items-stretch',
                cursorStyle,
                height,
                font,
                width,
                addtlContainerClasses
            )
        );
        setCurlyAlignStyle(
            flattenClassesToString('h-full rounded-tl rounded-bl', gapFixAlign, hoverBackgroundColor, backgroundColor)
        );
        setCurlyAlignSvgStyle(flattenClassesToString('h-full', 'invisible', fillColor));
        setGapStyleLeft(
            flattenClassesToString(
                'flex-grow flex-shrink',
                gapSize,
                gapFixGapLeft,
                font,
                hoverBackgroundColor,
                backgroundColor
            )
        );
        setContentStyle(
            flattenClassesToString(
                'inline-flex justify-center items-center text-center flex-nowrap whitespace-no-wrap',
                hoverColor,
                color,
                hoverBackgroundColor,
                backgroundColor,
                font,
                addtlContentClasses
            )
        );
        setGapStyleRight(
            flattenClassesToString(
                'flex-grow flex-shrink',
                gapSize,
                gapFixGapRight,
                font,
                hoverBackgroundColor,
                backgroundColor
            )
        );
        setCurlyImgStyle(flattenClassesToString('h-full', gapFixCurly));
        setCurlySvgStyle(flattenClassesToString('h-full', hoverFillColor, fillColor));

        setHref(props.href ? props.href : '#0');
    };

    useEffect(() => {
        renderStyles();
    }, [props]);

    const onClick = event => {
        if (props.disabled === true) {
            event.preventDefault();
            return;
        }

        if (!props.href) {
            event.preventDefault();
        }

        if (props.onClick) {
            props.onClick(event);
        } else if (props.type === 'submit') {
            const submitButton = document.getElementById(submitButtonId);
            submitButton.click();
        }

        return;
    };

    const boonButtonFragment = (
        <React.Fragment>
            <div className={curlyAlignStyle}>
                <svg fill="currentColor" className={curlyAlignSvgStyle} viewBox="0 0 30 125"></svg>
            </div>
            <div className={gapStyleLeft}></div>
            <div className={contentStyle}>
                <div>{props.children}</div>
            </div>
            <div className={gapStyleRight}></div>
            <div className={curlyImgStyle}>
                <svg fill="currentColor" className={curlySvgStyle} viewBox="0 0 40 125">
                    <path d="M41.79,62.65C26.83,61,27.09,40.81,25,32.71,21.19,9.82,12.33,1.27.37,0H-291.45V125.3H.37c12-1.27,20.82-9.82,24.59-32.71,2.13-8.1,1.87-28.3,16.83-29.94"></path>
                </svg>
            </div>
        </React.Fragment>
    );

    return (
        <React.Fragment>
            <style jsx>{styles}</style>
            {props.type === 'submit' ? (
                <React.Fragment>
                    <button id={submitButtonId} className="block absolute invisible" type="submit"></button>
                    <a href={href} onClick={onClick} className={containerStyle}>
                        {boonButtonFragment}
                    </a>
                </React.Fragment>
            ) : props.type === 'link' ? (
                <Link href={href} as={props.as}>
                    <a onClick={onClick} className={containerStyle}>
                        {boonButtonFragment}
                    </a>
                </Link>
            ) : (
                <a href={href} onClick={onClick} className={containerStyle}>
                    {boonButtonFragment}
                </a>
            )}
        </React.Fragment>
    );
};

export default BoonButton;
